import { capture } from '@/handlers/error';
import { useLessonFolder } from '@/hooks/useLessonFolder/useLessonFolder';
import { LessonFolder } from '@/models/lessonFolder';
import { useSchool, useSession } from '@/providers/session/SessionProvider';
import { isValidEmail } from '@/utils/validation';
import { useLazyQuery } from '@apollo/client';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GetTeachersResponse } from '../LessonSharingDialog/gql';
import { GET_TEACHERS, GetTeachersVariables } from './gql';

interface User {
    name: string;
    email: string;
}

type SharingRole = 'owner' | 'viewer';

const UserInfo = ({ name, email }: User) => {
    return (
        <Box sx={{ display: 'flex', flex: 1, flexShrink: 2, flexDirection: 'row', gap: 1.5, alignItems: 'center' }}>
            <AccountCircleIcon fontSize='large' />
            <Box display='grid'>
                <Typography noWrap>{name}</Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                    {email}
                </Typography>
            </Box>
        </Box>
    );
};

const UserAccess = ({
    name,
    email,
    role,
    onChange,
}: User & {
    role?: SharingRole;
    onChange?: (user: User, role: SharingRole | null) => Promise<void>;
}) => {
    const notifications = useNotifications();
    const { t } = useTranslation();
    const [removing, setRemoving] = useState(false);

    const handleRemove = async () => {
        setRemoving(true);
        try {
            await onChange?.({ name, email }, null);
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
            capture(error);
        } finally {
            setRemoving(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UserInfo name={name} email={email} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {role === 'owner' ? (
                    <Button disabled>{t('common.sharing.owner')}</Button>
                ) : (
                    role === 'viewer' && (
                        <Button
                            onClick={handleRemove}
                            loading={removing}
                            sx={(theme) => ({
                                color: (theme.vars || theme).palette.warning.main,
                                ...theme.applyStyles('dark', {
                                    color: (theme.vars || theme).palette.warning.light,
                                }),
                            })}
                        >
                            {t('common.remove')}
                        </Button>
                    )
                )}
            </Box>
        </Box>
    );
};

export const LessonFolderSharingDialog: FC<
    {
        folder: LessonFolder;
        onShared?: (users: { id: string; email: string }[]) => void;
        onCancel?: () => void;
    } & DialogProps
> = ({ folder, onShared, onCancel, ...props }) => {
    // general hooks
    const notifications = useNotifications();
    const { t } = useTranslation();
    const { schools } = useSchool();
    const { session } = useSession();
    const { share: shareLessonFolder, unshare: unshareLessonFolder, lessonFolder: latestLessonFolder, loading: loadingLessonFolder } = useLessonFolder(folder.id);

    // ui state
    const schoolOptions = useMemo(() => {
        return schools ? [...schools].sort((a, b) => (a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1)) : [];
    }, [schools]);
    const [emails, setEmails] = useState<string[]>([]);
    const [confirming, setConfirming] = useState(false);
    const [selectedSchool, setSelectedSchool] = useState(schoolOptions.length > 0 ? schoolOptions[0]?.id : undefined);

    // gql
    const [getTeachers, { data: teachersData, loading: loadingTeachers }] = useLazyQuery<GetTeachersResponse, GetTeachersVariables>(GET_TEACHERS, { fetchPolicy: 'network-only' });

    const hasInvalidEmail = emails.some((email) => !isValidEmail(email));

    // filter out current user and already selected emails
    const teacherOptions = teachersData?.school?.teachers
        ? teachersData.school.teachers
              .filter((teacher) => {
                  const isMe = teacher.user.email === session?.user?.email;
                  const isOwner = teacher.user.email === latestLessonFolder?.teacher.user.email;
                  const isAlreadyShared = latestLessonFolder?.shares?.some((s) => (s.user?.email || s.email) === teacher.user.email) ?? false;
                  const isSelected = emails.includes(teacher.user.email);
                  return !isMe && !isOwner && !isAlreadyShared && !isSelected;
              })
              .map((teacher) => ({
                  name: teacher.name,
                  email: teacher.user.email,
              }))
        : [];

    useEffect(() => {
        if (selectedSchool) {
            getTeachers({
                variables: {
                    where: {
                        id: selectedSchool,
                    },
                },
            });
        }
    }, [getTeachers, schools, selectedSchool]);

    return (
        <Dialog {...props}>
            <DialogTitle>
                {t('lesson.actions.share')} "{folder.name}"
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', my: 1 }}>
                    <FormControl>
                        <InputLabel id='lesson-sharing-school-select-label'>{t('common.school')}</InputLabel>
                        <Select
                            labelId='lesson-sharing-school-select-label'
                            label={t('common.school')}
                            value={selectedSchool}
                            onChange={(event) => setSelectedSchool(event.target.value)}
                            sx={{
                                flex: 1,
                                maxWidth: 200,
                                borderBottomRightRadius: 0,
                                borderTopRightRadius: 0,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderRightColor: 'transparent',
                                },
                            }}
                        >
                            {schoolOptions.map((school) => (
                                <MenuItem key={school.id} value={school.id}>
                                    {school.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Divider orientation='vertical' flexItem sx={{ borderColor: 'divider' }} />
                    <Autocomplete
                        multiple
                        limitTags={2}
                        freeSolo
                        filterSelectedOptions
                        filterOptions={(options, { inputValue }) => {
                            return options.filter((option) => {
                                return option.name.toLowerCase().includes(inputValue.toLowerCase()) || option.email.toLowerCase().includes(inputValue.toLowerCase());
                            });
                        }}
                        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('common.sharing.addPeople')}
                                placeholder={t('common.sharing.addPeople')}
                                error={hasInvalidEmail}
                                helperText={hasInvalidEmail ? t('common.form.invalidEmail') : ''}
                                slotProps={{
                                    input: {
                                        ...params.InputProps,
                                        endAdornment: (
                                            <Fragment>
                                                {loadingTeachers ? <CircularProgress color='inherit' size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </Fragment>
                                        ),
                                        sx: {
                                            borderBottomLeftRadius: 0,
                                            borderTopLeftRadius: 0,
                                        },
                                    },
                                }}
                                sx={{
                                    height: '100%',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderLeftColor: 'transparent',
                                    },
                                }}
                                fullWidth
                            />
                        )}
                        renderOption={({ key, ...props }, option) => {
                            return (
                                <li key={key} {...props}>
                                    <UserInfo name={option.name} email={option.email} />
                                </li>
                            );
                        }}
                        loading={loadingTeachers}
                        options={teacherOptions ?? []}
                        onChange={(_, value) => {
                            const newEmails = value.map((v) => (typeof v === 'string' ? v.trim() : v.email));
                            const uniqueEmails = Array.from(new Set(newEmails));
                            const filteredEmails = uniqueEmails.filter((email) => {
                                const isMe = email === session?.user?.email;
                                const isOwner = email === latestLessonFolder?.teacher.user.email;
                                const isAlreadyShared = latestLessonFolder?.shares?.some((s) => (s.user?.email || s.email) === email) ?? false;
                                return !isMe && !isOwner && !isAlreadyShared;
                            });
                            setEmails(filteredEmails);
                        }}
                        sx={{ flex: 1 }}
                    />
                </Box>
                {loadingLessonFolder ? (
                    <Box>
                        <Skeleton>
                            <Typography sx={{ mt: 2 }}>..........................</Typography>
                        </Skeleton>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                            <Skeleton>
                                {/* fill in some placholder info just so skeleton can calculate the widht and height */}
                                <UserAccess name={'John Doe'} email={'john.doe@placeholder.com'} role={'owner'} />
                            </Skeleton>
                        </Stack>
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={{ mt: 2 }}>
                            {latestLessonFolder?.shares.length} {t('common.sharing.folder.access')}
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 2 }}>
                            <UserAccess
                                name={`${latestLessonFolder?.teacher.user.name}${latestLessonFolder?.teacher.user.email === session?.user?.email ? ' (you)' : ''}`}
                                email={latestLessonFolder?.teacher.user.email || ''}
                                role={'owner'}
                            />
                            {latestLessonFolder?.shares &&
                                latestLessonFolder.shares.map((share, index) => (
                                    <UserAccess
                                        email={share.user?.email || share.email}
                                        name={share.user?.name || share.email}
                                        role={'viewer'}
                                        key={`${share.email}-${index}`}
                                        onChange={async (user, role) => {
                                            if (role === null) {
                                                // remove access
                                                try {
                                                    await unshareLessonFolder([share.id]);
                                                    onShared?.(latestLessonFolder.shares.filter((s) => s.email !== user.email));
                                                } catch (error) {
                                                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                                    capture(error);
                                                }
                                            }
                                        }}
                                    />
                                ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        onCancel?.();
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    variant='contained'
                    onClick={async () => {
                        setConfirming(true);
                        try {
                            const receivers = await shareLessonFolder(emails);
                            onShared?.([...(latestLessonFolder?.shares ?? []), ...receivers]);
                        } catch (error) {
                            capture(error);
                            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                        } finally {
                            setConfirming(false);
                        }
                    }}
                    loading={confirming}
                    disabled={emails.length === 0 || hasInvalidEmail}
                >
                    {t('lesson.folder.actions.share')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
